import { useEffect } from 'react'
import { FieldErrors, Path, UseFormReturn, UseFormSetError } from 'react-hook-form'
import { FieldError, useFormContext } from 'react-hook-form'

import { Fetchable } from 'interfaces/fetchable'
import resolveErrorByCode from 'constants/errors-http'
import { NON_FIELD_ERRORS } from 'constants/errors'
import { collateFieldErrors, fetchFieldErrors } from 'helpers/errorHelpers'

type UseRequestErrorsResult = string[] | null


// FIELD ERRORS
// -----------------------------------------------------------------------------------------

export function useFieldError (name?: string): string | undefined {

  const formContext = useFormContext()
  if (!formContext) {
    return
  }

  const errors: FieldErrors = formContext.formState.errors
  if (!name) {
    const fieldErrors = fetchFieldErrors(errors)
    const errorArray = collateFieldErrors(fieldErrors)

    return !!errorArray ? errorArray[0] : undefined // TODO - there may be more than 1 error in this array
  }

  const fieldError: FieldError = !!errors && errors[name]

  return fieldError?.message
}

/**
 * For passing errors to the UI even if the form's input fields are not visible.
*/
export function useFieldErrors<FormType> (formContext: UseFormReturn<FormType>): string | undefined {

  const errors: FieldErrors = formContext.formState.errors
  const fieldErrors = fetchFieldErrors(errors)
  const errorArray = collateFieldErrors(fieldErrors)

  return !!errorArray ? errorArray[0] : undefined // TODO - there may be more than 1 error in this array
}


// REQUEST ERRORS
// -----------------------------------------------------------------------------------------

type UseRequestErrorParams<Type> = {
  request: Fetchable<any>
  setError?: UseFormSetError<Type>
  fallBackToRest?: boolean
}

export function useRequestErrors<Type> (params: UseRequestErrorParams<Type>): UseRequestErrorsResult {
  const { request, setError, fallBackToRest } = params
  const { errorsMap, errorCode } = request

  useRequestErrorsInForm(request, setError)
  const requestErrors = !!errorsMap ? errorsMap[NON_FIELD_ERRORS] : null
  const restError = fallBackToRest && !!errorCode ? [resolveErrorByCode(errorCode)] : null

  return requestErrors || restError
}

// COMBINED
// -----------------------------------------------------------------------------------------
/**
 * Takes any errors returned from an API request
 * and injects them into the react-hook-form form context
 * to render directly on to input fields
 */
export function useRequestErrorsInForm<Type> (request: Fetchable<any>, setError?: UseFormSetError<Type>) {
  const { isLoading, errorsMap } = request

  useEffect(() => {
    if (isLoading || !errorsMap || !setError) {
      return
    }

    const keys = Object.keys(errorsMap) as Array<Path<Type>>
    if (!keys || !keys.length) {
      return
    }

    keys.forEach((key) => {
      const error = errorsMap[key][0]
      setError(key, {
        type: error,
        message: error,
      })
    })
    return
  }, [errorsMap, isLoading, setError])
}
